/**
 * SettingsScreen — user-facing options.
 *
 * Two background option groups, each independent but only one
 * selection active at a time:
 *   1. Painted Backdrop — full-bleed scene (e.g. Japanese Tableau)
 *      that REPLACES the kozo washi page bg. Dimmed in CSS to 50%
 *      so cards on top stay readable.
 *   2. Paper Tint — rebinds the five bokashi dye-pool colors of
 *      the classic kozo+bokashi sheet.
 *
 * Persists the chosen tint to localStorage and applies it globally
 * via the .paper--<id> modifier class on <html>, so every .paper
 * descendant retints through the CSS variable cascade or — for
 * painting kinds — swaps to the image background.
 *
 * Reachable at /?settings. Future option groups (audio, motion,
 * gameplay difficulty) plug into this same screen as additional
 * <section> blocks.
 */
import { useEffect, useState, type ReactElement } from 'react'
import {
  BOKASHI_TINTS,
  PAINTING_TINTS,
  type PaperTint,
  type TintId,
  loadTint,
  setTint,
} from './paperTint'
import { useSoundSettings, playSfx, setAmbience, SHOP_BEDS, BATTLE_BEDS } from '../sound'
import { resetShopTutorial, requestTutorialReplay } from './shopTutorial'
import './SettingsScreen.css'

export function SettingsScreen() {
  const [tint, setLocalTint] = useState<TintId>(() => loadTint())
  const { volume, muted, shopBed, battleBed, setVolume, toggleMuted, setShopBed, setBattleBed } =
    useSoundSettings()
  // Which bed is being previewed in the menu ('shop' | 'battle' | null).
  const [preview, setPreview] = useState<'shop' | 'battle' | null>(null)

  function chooseBed(scene: 'shop' | 'battle', id: string): void {
    if (scene === 'shop') setShopBed(id)
    else setBattleBed(id)
    // The ambience controller live-swaps a playing bed; if we're
    // previewing this scene, that crossfade happens automatically.
    // Picking 'off' stops the preview for that scene.
    if (id === 'off' && preview === scene) {
      setAmbience(null)
      setPreview(null)
    }
  }
  function togglePreview(track: 'shop' | 'battle'): void {
    if (preview === track) {
      setAmbience(null)
      setPreview(null)
    } else {
      setAmbience(track)
      setPreview(track)
    }
  }
  // Stop any preview when leaving the screen.
  useEffect(() => () => setAmbience(null), [])

  const choose = (id: TintId) => {
    setLocalTint(id)
    setTint(id)
  }

  const goBack = () => {
    // Back to wherever the user was; for the title-screen entry
    // we land at the bare root so the title's theme resolver picks up.
    window.history.length > 1 ? window.history.back() : window.location.assign('/')
  }

  return (
    <div className="paper settings-page">
      <header className="settings-page__head">
        <button className="settings-page__back hand-note" onClick={goBack} aria-label="Back">
          ← back
        </button>
        <span className="tiny-cap">options · 設定</span>
        <h1 className="cute-title">Folding Room Settings</h1>
        <p className="hand-note settings-page__lede">
          Adjust the surface, the chrome, and the rituals of the table.
          The choices here persist on this device.
        </p>
      </header>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2 className="cute-title">Painted Backdrop</h2>
          <p className="hand-note">
            A full-bleed scene that fills the page beneath every card and
            menu. Replaces the kozo washi paper with a painted view, dimmed
            so cards on top stay readable.
          </p>
        </header>
        <div className="tint-grid">
          {PAINTING_TINTS.map((option) => (
            <TintSwatch
              key={option.id}
              option={option}
              active={option.id === tint}
              onPick={choose}
            />
          ))}
        </div>
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2 className="cute-title">Paper Tint</h2>
          <p className="hand-note">
            The dye-pool color story painted into the kozo washi sheet —
            the classic paper-and-bokashi look. Each option recolors the
            same five dye pools without changing the underlying texture.
          </p>
        </header>

        <div className="tint-grid">
          {BOKASHI_TINTS.map((option) => (
            <TintSwatch
              key={option.id}
              option={option}
              active={option.id === tint}
              onPick={choose}
            />
          ))}
        </div>

        <p className="hand-note settings-section__foot">
          Comparing them side-by-side?{' '}
          <a href="/?tints" className="settings-link">Open the tint atelier →</a>
        </p>
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2 className="cute-title">Sound</h2>
          <p className="hand-note">
            Paper-and-percussion sound effects for shop actions,
            combines, and battle. Master volume and mute.
          </p>
        </header>

        <div className="settings-sound">
          <button
            type="button"
            className={`cute-btn settings-sound__mute${muted ? ' settings-sound__mute--on' : ''}`}
            onClick={toggleMuted}
          >
            {muted ? '🔇 muted' : '🔊 sound on'}
          </button>
          <label className="settings-sound__vol">
            <span className="tiny-cap">volume</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={volume}
              disabled={muted}
              onChange={(e) => {
                setVolume(Number(e.target.value))
                playSfx('ui-tap')
              }}
            />
            <span className="settings-sound__num">{Math.round(volume * 100)}</span>
          </label>
        </div>

        <p className="hand-note settings-section__foot">
          Auditioning the full library?{' '}
          <a href="/?sfx" className="settings-link">Open the SFX test bench →</a>
        </p>
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2 className="cute-title">Shop Music</h2>
          <p className="hand-note">
            The looping bed that plays while you draft in the shop.
            Preview to hear the current pick; it crossfades live.
          </p>
        </header>

        <div className="settings-beds">
          {SHOP_BEDS.map((b) => (
            <button
              key={b.id}
              type="button"
              className={`settings-bed${shopBed === b.id ? ' settings-bed--on' : ''}`}
              onClick={() => chooseBed('shop', b.id)}
            >
              {b.label}
            </button>
          ))}
          <button
            type="button"
            className={`settings-bed${shopBed === 'off' ? ' settings-bed--on' : ''}`}
            onClick={() => chooseBed('shop', 'off')}
          >
            Off
          </button>
        </div>

        <div className="settings-bed-preview">
          <button
            type="button"
            className="cute-btn"
            disabled={shopBed === 'off'}
            onClick={() => togglePreview('shop')}
          >
            {preview === 'shop' ? '⏹ stop preview' : '▶ preview shop'}
          </button>
        </div>
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2 className="cute-title">Battle Music</h2>
          <p className="hand-note">
            The looping bed during battle replays — taiko drums, koto,
            and shakuhachi. Preview without joining a battle.
          </p>
        </header>

        <div className="settings-beds">
          {BATTLE_BEDS.map((b) => (
            <button
              key={b.id}
              type="button"
              className={`settings-bed${battleBed === b.id ? ' settings-bed--on' : ''}`}
              onClick={() => chooseBed('battle', b.id)}
            >
              {b.label}
            </button>
          ))}
          <button
            type="button"
            className={`settings-bed${battleBed === 'off' ? ' settings-bed--on' : ''}`}
            onClick={() => chooseBed('battle', 'off')}
          >
            Off
          </button>
        </div>

        <div className="settings-bed-preview">
          <button
            type="button"
            className="cute-btn"
            disabled={battleBed === 'off'}
            onClick={() => togglePreview('battle')}
          >
            {preview === 'battle' ? '⏹ stop preview' : '▶ preview battle'}
          </button>
        </div>
      </section>

      <section className="settings-section">
        <header className="settings-section__head">
          <h2 className="cute-title">How to Play</h2>
          <p className="hand-note">
            Replay the guided shop tutorial — the spotlight tour that
            explains drafting, the card numbers, and synergies.
          </p>
        </header>
        <button
          type="button"
          className="cute-btn"
          onClick={() => {
            playSfx('ui-tap')
            // Clear the seen flag and request a force-open, then soft-nav
            // to the solo shop where SoloPlayScreen consumes the request.
            resetShopTutorial()
            requestTutorialReplay()
            window.history.pushState(null, '', '?solo')
            window.dispatchEvent(new Event('url-changed'))
          }}
        >
          Replay shop tutorial →
        </button>
      </section>
    </div>
  )
}

function TintSwatch({
  option,
  active,
  onPick,
}: {
  option: PaperTint
  active: boolean
  onPick: (id: TintId) => void
}): ReactElement {
  const swatchClass = `tint-swatch paper paper--${option.id}${active ? ' tint-swatch--active' : ''}`
  return (
    <button
      type="button"
      className={swatchClass}
      onClick={() => onPick(option.id as TintId)}
      aria-pressed={active}
    >
      <span className="tint-swatch__sheet" aria-hidden="true" />
      <span className="tint-swatch__body">
        <span className="tint-swatch__name">{option.name}</span>
        <span className="tint-swatch__sub hand-note">{option.subtitle}</span>
      </span>
      {active && (
        <span className="tint-swatch__check" aria-hidden="true">✓</span>
      )}
    </button>
  )
}
